/**
 * Baccarat game-domain types.
 * Ported from packages/amgbaccarat_v2/lib/core/constants/bet_constants.dart
 * + game_constants.dart.
 *
 * 7 bet areas (vs Dragon Tiger's 3):
 *   main:  player=265, banker=266, tie=267
 *   side:  playerPair=268, bankerPair=269, perfectPair=270, eitherPair=271
 *
 * Payouts include stake (multiplier of the bet, not net win):
 *   Player       2.0  (1:1)
 *   Banker       1.95 (0.95:1 with 5% commission)
 *   Tie          9.0  (8:1)
 *   PlayerPair   12.0 (11:1)
 *   BankerPair   12.0 (11:1)
 *   PerfectPair  26.0 (25:1)
 *   EitherPair   6.0  (5:1)
 *
 * The server is authoritative for win/lose; these client-side payout multipliers
 * are reference only. Tie pushes Player/Banker bets (returns stake).
 */

export type GamePhase =
  | 'idle'
  | 'initializing'
  | 'modeSelection'
  | 'betting'
  | 'bettingClosed'
  | 'waitingResult'
  | 'cardReveal'
  | 'showResult'
  | 'result'
  | 'newRound'
  | 'error';

export type BetType =
  | 'player'
  | 'banker'
  | 'tie'
  | 'playerPair'
  | 'bankerPair'
  | 'perfectPair'
  | 'eitherPair';

export const BET_ITEM_ID: Record<BetType, number> = {
  player: 265,
  banker: 266,
  tie: 267,
  playerPair: 268,
  bankerPair: 269,
  perfectPair: 270,
  eitherPair: 271,
};

export const PAYOUT: Record<BetType, number> = {
  player: 1,        // 1:1 net
  banker: 0.95,     // 0.95:1 net (5% commission)
  tie: 8,           // 8:1 net
  playerPair: 11,   // 11:1 net
  bankerPair: 11,   // 11:1 net
  perfectPair: 25,  // 25:1 net
  eitherPair: 5,    // 5:1 net (reference only — server settles)
};

export const MAIN_BETS: ReadonlyArray<BetType> = ['player', 'banker', 'tie'];

export function betTypeFromId(id: number): BetType | null {
  switch (id) {
    case 265: return 'player';
    case 266: return 'banker';
    case 267: return 'tie';
    case 268: return 'playerPair';
    case 269: return 'bankerPair';
    case 270: return 'perfectPair';
    case 271: return 'eitherPair';
    default:  return null;
  }
}

/**
 * Main-round result — determines which side wins on Player/Banker/Tie bets.
 * Side bets (pairs) settle independently based on the actual cards.
 */
export type GameResult = 'player' | 'banker' | 'tie';

/**
 * History slot value — Baccarat encodes side-pair info too (0-9), but the
 * Pixi port simplifies to a tri-state win indicator like Dragon Tiger.
 */
export const GAME_RESULT_HISTORY_VALUE: Record<GameResult, 1 | 2 | 3> = {
  player: 1,
  banker: 2,
  tie: 3,
};

export function gameResultFromWinningBetIds(ids: number[]): GameResult {
  if (ids.includes(BET_ITEM_ID.tie)) return 'tie';
  if (ids.includes(BET_ITEM_ID.player)) return 'player';
  if (ids.includes(BET_ITEM_ID.banker)) return 'banker';
  // No main-bet hit (shouldn't happen) — default to tie.
  return 'tie';
}

export type CardSuit = 'clubs' | 'diamonds' | 'hearts' | 'spades';

export const SUIT_OFFSET: Record<CardSuit, number> = {
  clubs: 0,
  diamonds: 13,
  hearts: 26,
  spades: 39,
};

export interface GameConfig {
  minBet: number;
  maxBet: number;
  chipValues: readonly number[];
}

/**
 * One round's parsed result. Up to 3 cards per side (1-indexed slots);
 * 0 = absent.
 */
export interface GameRound {
  roundId: string;
  nextRoundId: string;
  /** Card numbers 1..52; index 0 = first card. 0 means absent. */
  playerCards: number[];
  bankerCards: number[];
  /** Score 0..9. */
  playerScore: number;
  bankerScore: number;
  /** Winning bet item IDs from the server's `@` list. */
  winningBetIds: number[];
  result: GameResult;
  totalWinLose: number;
  totalBet: number;
  positiveWinAmount: number;
  hasPositiveWin: boolean;
}

/**
 * Baccarat-engine clear-card code (which hands had a 3rd card).
 * Source: packages/amgbaccarat_v2/lib/domain/game_engine/baccarat_engine.dart:200-205.
 *   1 = both had 3rd card (3 + 3)
 *   2 = banker had 3rd card only (2 + 3)
 *   3 = player had 3rd card only (3 + 2)
 *   4 = neither had 3rd card (2 + 2)
 */
export function clearCardType(playerCount: number, bankerCount: number): 1 | 2 | 3 | 4 {
  if (playerCount === 3 && bankerCount === 3) return 1;
  if (playerCount === 2 && bankerCount === 3) return 2;
  if (playerCount === 3 && bankerCount === 2) return 3;
  return 4;
}

/**
 * Whether a side's hand forms a pair — i.e. the FIRST TWO cards dealt share a
 * rank. The third card (if any) never affects the pair side bet. Card numbers
 * are 1..52 with SUIT_OFFSET, so rank = (card - 1) % 13; 0 means an absent
 * slot. Used by the bead-plate history to mark banker-pair / player-pair dots.
 */
export function isPair(cards: readonly number[]): boolean {
  if (cards.length < 2) return false;
  const a = cards[0];
  const b = cards[1];
  if (a <= 0 || b <= 0) return false;
  return (a - 1) % 13 === (b - 1) % 13;
}
